// Hungarian dental vocabulary. All forms are folded (see fold.ts):
// "tizenhatos" matches "tizenhatos", "Tizenhatos" and "tízenhátos".

import { isValidTooth, type Surface } from '@/utils/dental';
import type {
  CodeRule,
  Lexicon,
  LocationWord,
  MaterialRule,
  NumberRead,
  PhraseHit,
  Token,
} from './types';

// ── Numbers ──────────────────────────────────────────────────

const UNITS: Record<string, number> = {
  egy: 1,
  ketto: 2,
  ket: 2,
  harom: 3,
  negy: 4,
  ot: 5,
  hat: 6,
  het: 7,
  nyolc: 8,
  kilenc: 9,
};

/** "hatos" = "the six" — a tooth position without a quadrant. */
const ADJ_UNITS: Record<string, number> = {
  egyes: 1,
  kettes: 2,
  harmas: 3,
  negyes: 4,
  otos: 5,
  hatos: 6,
  hetes: 7,
  nyolcas: 8,
  kilences: 9,
};

/** Tens as used inside compounds (tizen-hat = 16). */
const TENS: Record<string, number> = {
  tizen: 10,
  huszon: 20,
  harminc: 30,
  negyven: 40,
  otven: 50,
  hatvan: 60,
  hetven: 70,
  nyolcvan: 80,
};

/** Case endings that can follow a tooth number ("tizenhatoson", "16-osnál"). */
const CASE_SUFFIXES = new Set([
  '',
  'n',
  'on',
  'en',
  'an',
  't',
  'ot',
  'et',
  'at',
  'nal',
  'nel',
  'ra',
  're',
  'ba',
  'be',
  'ban',
  'ben',
  'nak',
  'nek',
  'tol',
  'rol',
  'bol',
  'ig',
  'hoz',
  'hez',
  'val',
  'vel',
  'al',
  'el',
  'e',
  'ert',
  'kent',
  'nal',
]);

const ADJ_SUFFIXES = ['os', 'es', 'as'];

// ── Location words for "felső jobb hatos" ────────────────────

const UPPER_WORDS = new Set(['felso', 'felsoben', 'felul', 'maxilla', 'maxillaris']);
const LOWER_WORDS = new Set(['also', 'alsoban', 'alul', 'mandibula', 'mandibularis']);
const RIGHT_WORDS = new Set(['jobb', 'jobboldali', 'jobbra', 'jobbon']);
const LEFT_WORDS = new Set(['bal', 'baloldali', 'balra', 'balon']);
const PRIMARY_WORDS = new Set(['tejfog', 'tejfogon', 'tejfognal', 'tejfogat']);

// ── Findings and procedures ──────────────────────────────────

// Order matters: the first rule that matches wins.
const CODE_RULES: CodeRule[] = [
  // "devitalizálás" is the procedure (as in Serbian); "devitális" the state, below.
  {
    code: 'root_canal',
    stems: ['gyokerkezel', 'gyokertom', 'gyokercsatorn', 'endodont', 'endo', 'devitaliz'],
  },
  {
    code: 'periapical',
    stems: ['periapikal', 'periapical', 'gyokercsucs', 'granulom', 'ciszt', 'gocos'],
  },
  // "gyökércsonk", "gyökérmaradvány", "radix"
  { code: 'root_remnant', stems: ['gyokercsonk', 'gyokermaradv', 'radix'] },
  // "devitális", "elhalt", "pulpanekrózis"
  { code: 'devital', stems: ['devital', 'avital', 'elhalt', 'nekro', 'pulpanekr'] },
  { code: 'caries', stems: ['karie', 'caries', 'szuvas', 'szuvasod', 'lyuk', 'odvas'] },
  { code: 'sealant', stems: ['barazdazar', 'fisszurazar', 'szilant', 'sealant'] },
  { code: 'inlay', stems: ['inlay', 'onlay', 'overlay', 'betet'] },
  {
    code: 'filling',
    stems: ['tomes', 'tomott', 'tomtuk', 'tomtem', 'tomve', 'tomni', 'tomend', 'restaur', 'plomb'],
  },
  {
    code: 'fracture',
    stems: [
      'torot',
      'torott',
      'tores',
      'toredez',
      'letort',
      'fraktur',
      'reped',
      'lepattan',
      'kipattan',
      'csorb',
    ],
  },
  { code: 'bridge_pontic', stems: ['hidtag', 'hidtest', 'pontic'] },
  { code: 'bridge_abutment', stems: ['hidpiller', 'piller'] },
  {
    code: 'bridge_abutment',
    exact: ['hid', 'hidat', 'hiddal', 'hidnak', 'hidban', 'hidhoz', 'hidas', 'hidja'],
    genericBridge: true,
  },
  { code: 'crown', stems: ['korona'] },
  { code: 'veneer', stems: ['hejkeram', 'veneer', 'hejat'], exact: ['hej', 'hejjal'] },
  {
    code: 'extraction',
    stems: [
      'extrah',
      'extrakc',
      'eltavolit',
      'kihuz',
      'foghuz',
      'huzas',
      'huzni',
      'huztuk',
      'huzand',
    ],
  },
  {
    code: 'missing',
    // "kiesett", "kiesve", "elveszett", "elvesztette", "foghiány"
    stems: ['hianyz', 'kies', 'elveszett', 'elveszt', 'elveszit', 'foghiany'],
    exact: ['hiany', 'hianya', 'hianyat'],
  },
  { code: 'implant', stems: ['implant', 'beultet'] },
  {
    code: 'mobility',
    stems: ['mozog', 'mozgathat', 'mobil', 'lazul', 'laza', 'lotyog', 'ingo'],
    exact: ['inog'],
  },
  { code: 'calculus', stems: ['fogko', 'fogkov', 'zubkov'] },
  { code: 'impacted', stems: ['retine', 'impakt', 'elakadt'] },
  { code: 'wear', stems: ['kopot', 'kopas', 'abrazi', 'erozi', 'attrici'] },
  { code: 'sound', exact: ['ep', 'epek', 'rendben'], stems: ['egeszseges'] },
];

const SURFACE_STEMS: [string, Surface][] = [
  ['mezi', 'M'],
  ['mesi', 'M'],
  ['diszt', 'D'],
  ['dist', 'D'],
  ['okklu', 'O'],
  ['occlu', 'O'],
  ['ragofel', 'O'],
  ['inciz', 'I'],
  ['incis', 'I'],
  ['metszoel', 'I'],
  ['bukk', 'B'],
  ['bucc', 'B'],
  ['veszt', 'B'],
  ['vest', 'B'],
  ['labi', 'B'],
  ['facia', 'B'],
  ['lingu', 'L'],
  ['palat', 'L'],
  ['oral', 'L'],
];

/** Lower-case abbreviations safe to accept (no clash with Hungarian words). */
const LOWERCASE_SURFACE_ABBR = new Set(['mod', 'mo', 'do', 'od', 'om', 'modb', 'modl']);

const MATERIAL_RULES: MaterialRule[] = [
  { material: 'metal_ceramic', stems: ['femkeram'] },
  { material: 'lithium_disilicate', stems: ['emax', 'e-max', 'litium', 'diszilikat'] },
  { material: 'zirconia', stems: ['cirkon', 'zirkon', 'zirconi'] },
  { material: 'glass_ionomer', stems: ['uvegionomer', 'glasionomer', 'ionomer', 'gic'] },
  { material: 'composite', stems: ['kompozit', 'composit', 'feher'] },
  { material: 'amalgam', stems: ['amalgam'] },
  { material: 'ceramic', stems: ['keram', 'porcelan'] },
  { material: 'gold', stems: ['arany'] },
  { material: 'titanium', stems: ['titan'] },
  { material: 'temporary', stems: ['ideiglenes', 'provizor'] },
];

const PLANNED_STEMS = ['tervez', 'szukseges', 'kell', 'javasolt', 'indikalt'];

/** Qualifiers kept as a note on the entry. */
const NOTE_WORDS: Record<string, string> = {
  szekunder: 'szekunder',
  melly: 'mély',
  mely: 'mély',
  kezdodo: 'kezdődő',
  regi: 'régi',
  elegtelen: 'elégtelen',
  hianyos: 'hiányos',
  szuvasodott: 'szuvasodott',
};

const NEGATION_WORDS = new Set(['nem', 'nincs', 'nincsen', 'sincs']);

const FILLER_WORDS = new Set([
  'a',
  'az',
  'fog',
  'fogon',
  'fogra',
  'fognal',
  'fogat',
  'foga',
  'fogak',
  'fogakon',
  'fogakat',
  'fogban',
  'felszin',
  'felszinen',
  'felszinet',
  'felszinu',
  'felszinek',
  'oldali',
  'oldalon',
  'oldal',
  'van',
  'vannak',
  'is',
  'es',
  'meg',
  'valamint',
  'illetve',
  'rajta',
  'ott',
  'itt',
  'pedig',
  'szinten',
  'lathato',
  'talalhato',
  'latszik',
  'uj',
  'egy',
  'fokozatu',
  'labiale',
  'ezen',
  'azon',
  'mar',
  'meglevo',
  'meglevo',
]);

/** Words that start a new clause, like a full stop. */
const SEPARATOR_WORDS = new Set(['majd', 'aztan', 'utana', 'tovabba', 'kovetkezo']);

// Whole-clause matches only: "elfogadható tömés…" must stay a finding.
const COMMAND_PATTERNS: Lexicon['commandPatterns'] = [
  {
    command: 'approve_all',
    pattern:
      /^(mindet |mind |az osszeset )?(jovahagyom|jovahagyas|elfogadom)( mindet| az osszeset| mind)?$/,
  },
  {
    command: 'undo',
    pattern: /^(visszavonom|visszavonas|torold az utolsot|torlom az utolsot|megse)$/,
  },
  { command: 'yes', pattern: /^igen$/ },
  { command: 'no', pattern: /^nem$/ },
];

const NO_CHANGE_PATTERN = /(valtozatlan|nincs valtozas|minden mas rendben)/;

// ── Number reading ───────────────────────────────────────────

const COMPOUNDS: [string, number, boolean][] = (() => {
  const list: [string, number, boolean][] = [];
  for (const [t, tv] of Object.entries(TENS)) {
    for (const [u, uv] of Object.entries(UNITS)) list.push([t + u, tv + uv, false]);
    for (const [u, uv] of Object.entries(ADJ_UNITS)) list.push([t + u, tv + uv, true]);
  }
  return list.sort((a, b) => b[0].length - a[0].length);
})();

function splitSuffix(rest: string): { adjectival: boolean; suffix: string } | null {
  if (CASE_SUFFIXES.has(rest)) return { adjectival: false, suffix: rest };
  for (const adj of ADJ_SUFFIXES) {
    if (rest.startsWith(adj) && CASE_SUFFIXES.has(rest.slice(adj.length))) {
      return { adjectival: true, suffix: rest.slice(adj.length) };
    }
  }
  return null;
}

function hit(value: number, suffix: string, adjectival: boolean): NumberRead {
  return {
    value,
    used: 1,
    adjectival,
    from: suffix === 'tol' || suffix === 'rol',
    to: suffix === 'ig',
  };
}

/** "16", "16-os", "16-osnál", "tizenhatos", "harminchatost" → 16 / 36. */
function readNumber(tokens: Token[], i: number): NumberRead | null {
  const norm = tokens[i].norm;
  const digits = /^(\d{2})(?:-?([a-z]+))?$/.exec(norm);
  if (digits) {
    const parts = splitSuffix(digits[2] ?? '');
    return parts ? hit(Number(digits[1]), parts.suffix, parts.adjectival) : null;
  }
  for (const [word, value, adjectival] of COMPOUNDS) {
    if (norm.startsWith(word) && CASE_SUFFIXES.has(norm.slice(word.length))) {
      return hit(value, norm.slice(word.length), adjectival);
    }
  }
  return null;
}

/** "hatos", "hatoson" → 6; "bölcsességfog" → 8. */
function readPosition(norm: string): number | null {
  if (norm.startsWith('bolcsessegfog')) return 8;
  for (const [word, value] of Object.entries(ADJ_UNITS)) {
    if (norm.startsWith(word) && CASE_SUFFIXES.has(norm.slice(word.length))) return value;
  }
  return null;
}

/** "egy-hatos" → 16, "négy-hetes" → 47. */
function readQuadrantPair(a: string, b: string): number | null {
  const q = UNITS[a];
  const p = readPosition(b);
  if (!(q && p) || q > 8) return null;
  const value = q * 10 + p;
  return isValidTooth(value) ? value : null;
}

function location(norm: string): LocationWord | null {
  if (UPPER_WORDS.has(norm)) return 'upper';
  if (LOWER_WORDS.has(norm)) return 'lower';
  if (RIGHT_WORDS.has(norm)) return 'right';
  if (LEFT_WORDS.has(norm)) return 'left';
  if (PRIMARY_WORDS.has(norm)) return 'primary';
  if (norm === 'oldali' || norm === 'a') return 'filler';
  return null;
}

function readPhrase(tokens: Token[], i: number): PhraseHit | null {
  const n = tokens[i].norm;
  const next = tokens[i + 1]?.norm ?? '';
  if (n === 'nem' && next.startsWith('vital')) return { kind: 'code', code: 'devital', used: 2 };
  if ((n === 'nincs' || n === 'nincsen') && next === 'meg')
    return { kind: 'code', code: 'missing', used: 2 };
  if (n === 'fem' && next.startsWith('keram'))
    return { kind: 'material', material: 'metal_ceramic', used: 2 };
  return null;
}

const GRADES: Record<string, string> = {
  '1': 'I',
  i: 'I',
  elso: 'I',
  '2': 'II',
  ii: 'II',
  masod: 'II',
  '3': 'III',
  iii: 'III',
  harmad: 'III',
};

/** "második fokú", "II. fokú", "2-es fokú". */
function readGrade(tokens: Token[], i: number): { grade: string; used: number } | null {
  if (!tokens[i + 1]?.norm.startsWith('fok')) return null;
  const word = tokens[i].norm.replace(/-?(es|os|as)$/, '');
  const key = Object.keys(GRADES).find((k) => word === k || (k.length > 3 && word.startsWith(k)));
  return key ? { grade: GRADES[key], used: 2 } : null;
}

export const hu: Lexicon = {
  lang: 'hu',
  readNumber,
  readQuadrantPair,
  readPosition,
  location,
  rangeFrom: new Set(),
  rangeTo: new Set(),
  readPhrase,
  codeRules: CODE_RULES,
  surfaceStems: SURFACE_STEMS,
  lowercaseSurfaceAbbr: LOWERCASE_SURFACE_ABBR,
  excludedSurfaceAbbr: new Set(),
  materialRules: MATERIAL_RULES,
  isPlanned: (n) =>
    PLANNED_STEMS.some((st) => n.startsWith(st)) ||
    (n.length > 6 && /(ando|endo)$/.test(n) && !n.startsWith('endo')),
  note: (n) => NOTE_WORDS[n] ?? null,
  negationWords: NEGATION_WORDS,
  correctionWords: new Set(['hanem']),
  fillerWords: FILLER_WORDS,
  separatorWords: SEPARATOR_WORDS,
  commandPatterns: COMMAND_PATTERNS,
  noChangePattern: NO_CHANGE_PATTERN,
  price: { unit: 'din[aá]r(?:t|ban|ral)?|rsd', thousand: 'ezer' },
  readGrade,
};
